import { useEffect, useRef, useState, memo } from "react";
import Emoji from "../../../../player-components/emoji/emoji";
import { useAppSelector } from "redux-share/hooks";
import { ConditionName, IMarker } from "interfaces";
import { conditions, inViewPort, progressBar, progressBarWidth, videoTag } from "helpers";
import { handleVideoTimeline } from "redux-share/actions/controller.actions";
import { eventMarkerClickLog, sendFluentLog } from "../../../../../../../logs";
import useOnClickOutside from "helpers/hooks/useOnClickOutside";
import { handleTimeTooltip } from "redux-share/actions/progressbar.actions";
import { shallowEqual } from 'react-redux'
import dynamic from "next/dynamic";

const ShareDialog = dynamic(() => import("../../../../player-components/share/shareDialog"));

const Markers = () => {

    const { hlsLiveSyncPosition, duration, activeElements } = useAppSelector(({ controller: { hlsLiveSyncPosition, duration, activeElements, isFullScreen } }) => ({ hlsLiveSyncPosition, duration, activeElements, isFullScreen }), shallowEqual);
    const { markers } = useAppSelector(({ videoData: { markers, isComplexPortrait, videoElementSizeInfo } }) => ({ markers, isComplexPortrait, videoElementSizeInfo }), shallowEqual);

    const [markerTime, setMarkerTime] = useState<number | undefined>(undefined);
    const [openDialog, setOpenDialog] = useState<boolean>(false);
    const [overlapMarkers, setOverlapMarkers] = useState<any[]>([]);
    const [overlapMarkersPosition, setOverlapMarkersPosition] = useState<any>({});
    const [isOverlapMarkersInViewport, setIsOverlapMarkersInViewport] = useState<boolean>(false);

    const durationTime = conditions(ConditionName.isLiveParam) ? hlsLiveSyncPosition : duration;

    const calculateMarkerPosition = (time: number) => durationTime ? ((progressBarWidth() * time) / durationTime) : 0;

    const handleClick = (event: any, markerTime: number, markerId: number, markerTitle: String, markerDescription: String, markerSticker: String, isTimer: boolean, markers:any) => {
        event.stopPropagation();
        const eventMarkerWidth = 24
        const result = (eventMarkerWidth * markerTime) / (progressBarWidth() + 5);

        let overlapMarkers:any[] = []
        let sumOverlapMarkersTime: number = 0;

        markers.map((marker:any) => {
            if ((marker.time > markerTime - result) && (marker.time < markerTime + result)) {
                overlapMarkers.push(marker);
                sumOverlapMarkersTime += marker.time
            }
        })
        if (overlapMarkers.length > 1) setOverlapMarkers(overlapMarkers);
        else {
            overlapMarkers=[];
            setOverlapMarkers([]);
            handleJumpToMarker(event, markerTime, markerId, markerTitle, markerDescription, markerSticker,isTimer);
        }
    }

    const handleJumpToMarker = (event: any, markerTime: number, markerId: number, markerTitle: String, markerDescription: String, markerSticker: String ,isTimer:boolean) => {
        event.stopPropagation();
        handleTimeTooltip(event, isTimer);
        handleVideoTimeline(videoTag().currentTime, markerTime);
        videoTag().currentTime = markerTime;
        if (progressBar()) progressBar().style.width = calculateMarkerPosition(markerTime);
        sendFluentLog(eventMarkerClickLog(markerId, markerTime, markerTitle, markerDescription, markerSticker));
    }

    useEffect(() => {
        if (!openDialog) setMarkerTime(undefined);
    }, [openDialog]);

    useEffect(() => {
        if (!!markerTime) setOpenDialog(true);
    }, [markerTime]);

    useEffect(() => {
        if(!conditions(ConditionName.isActiveElements)) setOverlapMarkers([])
    },[activeElements])

    const markerRef = useRef<any>(null)

    useOnClickOutside(markerRef, () => setOverlapMarkers([]));

    useEffect(() => {
        if(overlapMarkers.length > 0) {
            const isInViewPort = inViewPort(document.getElementsByClassName("pl-overlapMarkers")[0]);
            if (isInViewPort === "inViewport") {
                setIsOverlapMarkersInViewport(true);
                return setOverlapMarkersPosition({ left: calculateMarkerPosition(overlapMarkers[0].time) })
            } else if (isInViewPort === "OutViewPortFromLeft") {
                setIsOverlapMarkersInViewport(false);
                return setOverlapMarkersPosition({ left: 0 });
            } else if(isInViewPort === "OutViewPortFromRight") {
                setIsOverlapMarkersInViewport(false);
                return setOverlapMarkersPosition({ right: 0 });
            }
        }
    }, [overlapMarkers, document.getElementsByClassName("pl-overlapMarkers")[0]])

    const handleContextMenu = (event:any) => {
        event.preventDefault();
        if (conditions(ConditionName.isTouchScreen)) setMarkerTime(Number((event.target as any).getAttribute("data-marker")));
    }

    return (
        <>
            {markers.map((marker: any) => (
                <div
                    onContextMenu={handleContextMenu}
                    key={marker.id}
                    data-marker={marker.time}
                    onClick={(event) => handleClick(event, marker.time, marker.id, marker.title, marker.description, marker.sticker, true, markers)}
                    onDoubleClick={(event: any) => event.stopPropagation()}
                >
                    <div
                        className={`pl-emoji-image pl-absolute pl-bottom-[200%] pl-bg-z-gray-700/[0.4] pl-rounded-[6px] pl-p-1 ${conditions(ConditionName.isTouchScreenPortrait) ? "pl-mb-3" : "pl-mb-5"} pl-ml-[-12px] pl-cursor-pointer`}
                        style={{ left: calculateMarkerPosition(marker.time) }} data-marker={marker.time}>
                        {(marker.isPersonal !== null && marker.isPersonal !== undefined) && <div className="pl-absolute pl-rounded-full pl-top-[-2px] pl-right-[-1px] pl-bg-main pl-w-2 pl-h-2"/>}
                        <Emoji dataMarker={marker.time} name={marker.sticker} className="pl-w-[16px] pl-h-[16px] !pl-max-w-max" />
                    </div>
                </div>
            ))}
            {overlapMarkers.length > 0 && (
                <div
                    className={`pl-overlapMarkers ${isOverlapMarkersInViewport ? "pl-overlayMarkersInViewport" : "pl-overlayMarkersOutViewport"} pl-absolute pl-bottom-[45px] pl-px-1 pl-py-2 pl-flex pl-gap-3 pl-flex-row-reverse pl-justify-end pl-bg-z-gray-700/[0.4] pl-rounded-[6px] pl-ml-[-10px] animate__animated animate__fadeInUp pl-opacity-100`}
                    ref={markerRef}
                    style={overlapMarkersPosition}
                >
                    {overlapMarkers.map((overlapMarker: any) => (
                        <div
                            key={overlapMarker.id}
                            className="pl-emoji-image pl-rounded-[6px] pl-cursor-pointer"
                            data-marker={overlapMarker.time}
                            onClick={(event: any) => handleJumpToMarker(event, overlapMarker.time, overlapMarker.id, overlapMarker.title, overlapMarker.description, overlapMarker.sticker, true)}
                            onDoubleClick={(event: any) => event.stopPropagation()}
                        >
                            <Emoji dataMarker={overlapMarker.time} name={overlapMarker.sticker} className="pl-w-[24px] pl-h-[24px] !pl-max-w-max" />
                        </div>
                    ))}
                </div>
            )}
            {!!openDialog && <ShareDialog currentTime={markerTime ? markerTime : undefined} status={openDialog} onClose={setOpenDialog} markerId={((markers.filter((item: IMarker) => item.time === markerTime))[0]).id} />}
        </>
    )
}

export default memo(Markers);